import type { TFunction } from "i18next";
import type { CityHallStateResponse } from "../../client";
import type { UserData } from "../../models/user";
import type { MapObject } from "../../models/mapObject";
import { MetricTile } from "../../ui/cityhall/MetricChartModal";
import { format_num } from "../../utils/format/format_num";
import ElderWelcomeBanner from "./ElderWelcomeBanner";
import MayorActionList from "./MayorActionList";
import MetricTilesCard from "./MetricTilesCard";

interface MainScreenProps {
  formatElectionTimeRemaining: (electionEndAt: string | null | undefined) => string;
  freePlots: MapObject[];
  handleCompleteElection: () => void;
  handleEnterInterior: () => void;
  handleGoToFreePlot: () => void;
  handleRemoveMayor: () => void;
  isLoading: boolean;
  setCurrentScreen: (screen: "talk_to_mayor" | "register" | "vote_list" | "mayor_info" | "mayor_role" | "elections_info") => void;
  state: CityHallStateResponse | undefined;
  t: TFunction;
  userData: UserData | undefined;
}

function MainScreen({
  formatElectionTimeRemaining,
  freePlots,
  handleCompleteElection,
  handleEnterInterior,
  handleGoToFreePlot,
  handleRemoveMayor,
  isLoading,
  setCurrentScreen,
  state,
  t,
  userData,
}: MainScreenProps) {
  return (
        <div
          className="cityhall-scrollable absolute top-0 left-1/2 bottom-0 z-[10004] flex w-full max-w-[332px] -translate-x-1/2 flex-col gap-3xl overflow-x-hidden overflow-y-auto pt-[54px] pb-2xl"
        >
          <ElderWelcomeBanner
            state={state}
            formatElectionTimeRemaining={formatElectionTimeRemaining}
            t={t}
          />

          {/* Compact stats card — population metrics (stays under the dialog) */}
          {state && (
            <MetricTilesCard>
              <MetricTile
                label={t("Received citizenship")}
                hint={t("Total number of players who have ever joined the game.")}
                icon="/ui/cityhall/citizenship.png"
                value={state.total_citizens}
                series={state.citizens_history ?? []}
                formatValue={(v) => format_num(Math.round(v))}
              />
              <MetricTile
                label={t("Residents")}
                hint={t("Players currently living in houses on the islands.")}
                icon="/ui/cityhall/residents.png"
                value={state.residents}
                series={state.residents_history ?? []}
                formatValue={(v) => format_num(Math.round(v))}
              />
            </MetricTilesCard>
          )}

          <MayorActionList
            handleEnterInterior={handleEnterInterior}
            handleCompleteElection={handleCompleteElection}
            handleRemoveMayor={handleRemoveMayor}
            setCurrentScreen={setCurrentScreen}
            isLoading={isLoading}
            electionActive={state?.election_active}
            hasMayor={state?.has_mayor}
            isAdmin={userData?.is_admin}
            t={t}
          />

          {/* Real-estate metrics — moved to the very bottom, under all options */}
          {state && (
            <MetricTilesCard>
              <MetricTile
                label={t("Free plots")}
                hint={t("Number of island plots that don't have an owner yet.")}
                icon="/ui/cityhall/free-plots.png"
                value={state.free_plots ?? freePlots.length}
                series={state.free_plots_history ?? []}
                formatValue={(v) => format_num(Math.round(v))}
                actionLabel={freePlots.length > 0 ? t("Go to a free plot") : undefined}
                onAction={freePlots.length > 0 ? handleGoToFreePlot : undefined}
              />
              <MetricTile
                label={t("Abandonment")}
                hint={t("Shows the overall share of abandoned houses that are slowly filling up with cobwebs.")}
                icon="/ui/cityhall/abandonment.png"
                value={state.abandonment_pct ?? 0}
                series={state.abandonment_pct_history ?? []}
                formatValue={(v) => `${Math.round(v * 10) / 10}%`}
              />
            </MetricTilesCard>
          )}
        </div>
  );
}

export default MainScreen;
